import { useRef } from 'react';
import { useTableState } from 'react-stately';
import { useTable } from 'react-aria';
import {
    TableCell,
    TableCheckboxCell,
    TableColumnHeader,
    TableHeaderRow,
    TableRow,
    TableRowGroup,
    TableSelectAllCell
} from 'client/components/aria/Table/index';
import PropTypes from 'prop-types';
import { uniqueId } from 'lodash';
import styles from './Table.module.scss';

const Table = (props) => {
    const { selectionMode, selectionBehavior, caption, captionSize, className, hideHeader } = props;
    const state = useTableState({
        ...props,
        isKeyboardNavigationDisabled: false,
        showSelectionCheckboxes: selectionMode === 'multiple' &&
            selectionBehavior !== 'replace'
    });

    const ref = useRef();
    const { collection } = state;
    const { gridProps } = useTable(props, state, ref);
    const testId = props['data-testid'] ?? 'table';

    return (
        <table
            {...gridProps}
            ref={ref}
            className={[
                'govuk-table',
                styles.table,
                hideHeader && styles.hiddenHeader,
                className
            ].filter(Boolean).join(' ')}
            data-testid={testId}
        >
            {caption && <caption
                className={`govuk-table__caption govuk-!-margin-top-2 govuk-!-margin-bottom-2 ${captionSize ? `govuk-table__caption--${captionSize}` : 'govuk-table__caption--m'}`}>
                {caption}
            </caption>}
            <TableRowGroup
                type="thead"
                className="govuk-table__head"
                data-testid={`${testId}.thead`}
            >
                {collection?.headerRows.map((headerRow) => (
                    <TableHeaderRow
                        key={headerRow.key}
                        item={headerRow}
                        state={state}
                        data-testid={`${testId}.thead`}
                    >
                        {[...headerRow.childNodes].map((column) =>
                            column?.props?.isSelectionCell
                                ? (
                                    <TableSelectAllCell
                                        key={column.key}
                                        column={column}
                                        state={state}
                                        data-testid={`${testId}.thead`}
                                        className={column.props.className}
                                    />
                                )
                                : (
                                    <TableColumnHeader
                                        key={column.key}
                                        column={column}
                                        state={state}
                                        data-testid={`${testId}.thead`}
                                        className={column.props.className}
                                    />
                                )
                        )}
                    </TableHeaderRow>
                ))}
            </TableRowGroup>
            <TableRowGroup type="tbody" className="govuk-table__body" data-testid={`${testId}.tbody`}>
                {[...collection.body.childNodes].map((row, index) => (
                    <TableRow
                        key={row.key}
                        item={row}
                        state={state}
                        data-testid={`${testId}.tbody.row.${index}`}
                    >
                        {[...row.childNodes].map((cell) =>
                            cell?.props?.isSelectionCell
                                ? <TableCheckboxCell
                                    key={cell.key}
                                    cell={cell}
                                    state={state}
                                    data-testid={`${testId}.tbody.row.${index}`}
                                />
                                : <TableCell
                                    key={cell.key}
                                    cell={cell}
                                    state={state}
                                    data-testid={`${testId}.tbody.row.${index}`}
                                />
                        )}
                    </TableRow>
                ))}
            </TableRowGroup>
        </table>
    );
};

Table.propTypes = {
    'data-testid': PropTypes.string,
    'aria-label': PropTypes.string,
    'aria-labelledby': PropTypes.string,
    'aria-describedby': PropTypes.string,
    'aria-details': PropTypes.string,
    children: PropTypes.any,
    className: PropTypes.string,
    caption: PropTypes.string,
    captionSize: PropTypes.oneOf(['s', 'm', 'l', 'xl']),
    hideHeader: PropTypes.bool,
    selectionMode: PropTypes.oneOf(['single', 'multiple']),
    selectionBehavior: PropTypes.oneOf(['toggle', 'replace'])
};

Table.defaultProps = {
    'data-testid': 'table-' + uniqueId()
};

export default Table;
